(() => {
  class HeaderSearchController {
    constructor(header) {
      this.header = header;
      this.toggleButton = header.querySelector('[data-mobile-search-toggle]');
      this.panel = header.querySelector('[data-mobile-search-panel]');
      this.closeButton = header.querySelector('[data-mobile-search-close]');
      this.input = this.panel?.querySelector('[data-predictive-search-input]');
      this.predictiveSearch = this.panel?.querySelector('predictive-search');

      this.dialog = new window.DialogController({
        bodyClass: 'mobile-search-open',
        opener: this.toggleButton,
        panel: this.panel,
        onOpen: () => {
          this.header.scrollController?.reveal();
          window.requestAnimationFrame(() => {
            this.input?.focus({ preventScroll: true });
          });
        },
        onClose: () => {
          this.predictiveSearch?.close?.();
          this.input?.blur();
        }
      });

      this.handleOpen = this.handleOpen.bind(this);
      this.handleClose = this.handleClose.bind(this);
    }

    connect() {
      this.toggleButton?.addEventListener('click', this.handleOpen);
      this.closeButton?.addEventListener('click', this.handleClose);
    }

    disconnect() {
      this.toggleButton?.removeEventListener('click', this.handleOpen);
      this.closeButton?.removeEventListener('click', this.handleClose);
    }

    handleOpen() {
      this.header.menuController?.close();
      this.dialog.open();
    }

    handleClose() {
      this.close();
    }

    close(options = {}) {
      this.dialog.close(options);
    }

    isOpen() {
      return this.dialog.isOpen();
    }
  }

  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('[data-predictive-search-form]');
      this.input = this.querySelector('[data-predictive-search-input]');
      this.results = this.querySelector('[data-predictive-search-results]');
      this.predictiveUrl = this.form?.dataset.predictiveUrl || '/search/suggest';
      this.abortController = null;
      this.debounceTimer = null;

      this.handleInput = this.handleInput.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
    }

    connectedCallback() {
      if (!this.input || !this.results) return;

      this.input.addEventListener('input', this.handleInput);
      this.input.addEventListener('focus', this.handleInput);
      document.addEventListener('click', this.handleDocumentClick);
      document.addEventListener('keydown', this.handleDocumentKeydown);
    }

    disconnectedCallback() {
      this.input?.removeEventListener('input', this.handleInput);
      this.input?.removeEventListener('focus', this.handleInput);
      document.removeEventListener('click', this.handleDocumentClick);
      document.removeEventListener('keydown', this.handleDocumentKeydown);
      this.close();
    }

    handleDocumentClick(event) {
      if (!this.contains(event.target)) {
        this.close();
      }
    }

    handleDocumentKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
      }
    }

    handleInput() {
      window.clearTimeout(this.debounceTimer);

      const query = this.input.value.trim();

      if (query.length < 2) {
        this.close();
        return;
      }

      this.debounceTimer = window.setTimeout(() => {
        this.fetchResults(query);
      }, 180);
    }

    async fetchResults(query) {
      this.abortController?.abort();
      this.abortController = new AbortController();

      const url = `${this.predictiveUrl}.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,article&resources[limit]=4`;

      try {
        const response = await fetch(url, {
          signal: this.abortController.signal,
          headers: {
            Accept: 'application/json'
          }
        });

        if (!response.ok) throw new Error('Predictive search failed');

        const data = await response.json();
        this.renderResults(data.resources?.results || {});
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.renderEmpty();
        }
      }
    }

    renderResults(results) {
      const products = results.products || [];
      const collections = results.collections || [];
      const articles = results.articles || [];

      if (!products.length && !collections.length && !articles.length) {
        this.renderEmpty();
        return;
      }

      this.results.innerHTML = `
        ${this.renderGroup('Products', products, true)}
        ${this.renderGroup('Collections', collections)}
        ${this.renderGroup('Articles', articles)}
      `;

      this.results.hidden = false;
    }

    renderGroup(title, items, showPrice = false) {
      if (!items.length) return '';

      return `
        <div class="predictive-search__group">
          <p class="predictive-search__heading">${title}</p>

          ${items.map((item) => {
            const image = item.featured_image?.url || item.image || '';
            const price = item.price || item.price_min || '';

            return `
              <a href="${item.url}" class="predictive-search__item">
                <span class="predictive-search__image">
                  ${image ? `<img src="${image}" alt="" loading="lazy">` : ''}
                </span>

                <span>
                  <span class="predictive-search__title">${this.escapeHtml(item.title)}</span>
                  ${showPrice && price ? `<span class="predictive-search__price">${price}</span>` : ''}
                </span>
              </a>
            `;
          }).join('')}
        </div>
      `;
    }

    renderEmpty() {
      this.results.innerHTML = '<p class="predictive-search__empty">No results found.</p>';
      this.results.hidden = false;
    }

    close() {
      window.clearTimeout(this.debounceTimer);
      this.abortController?.abort();

      if (this.results) {
        this.results.hidden = true;
        this.results.innerHTML = '';
      }
    }

    escapeHtml(string) {
      return String(string)
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }
  }

  window.HeaderSearchController = HeaderSearchController;

  if (!customElements.get('predictive-search')) {
    customElements.define('predictive-search', PredictiveSearch);
  }
})();
